import { useEffect } from 'react'
import { useAuth } from '@/lib/auth-context'
import { config } from '@/lib/config'
import type { User } from '@/lib/api/auth'

/**
 * Canny Feedback Widget
 *
 * - Loads Canny SDK when configured
 * - Identifies the current user (if logged in)
 * - Exposes window.Canny for other modules (e.g. openCannyFeedback)
 */

export function CannyWidget() {
  const { user } = useAuth()

  useEffect(() => {
    if (!config.cannyAppId) {
      console.log('Canny: Not configured (VITE_CANNY_APP_ID not set)')
      return
    }

    // If SDK already loaded, just identify user
    if (window.Canny) {
      identifyUser(user)
      return
    }

    // Load Canny SDK script once
    ;(function (w: Window & typeof globalThis, d: Document) {
      if (w.Canny) return
      w.Canny = function (...args: unknown[]) {
        ;(w.Canny!.q = w.Canny!.q || []).push(args)
      }
      const script = d.createElement('script') as HTMLScriptElement
      script.async = true
      script.src = 'https://canny.io/sdk.js'
      const firstScript = d.getElementsByTagName('script')[0]
      firstScript?.parentNode?.insertBefore(script, firstScript)
    })(window, document)

    // Try to identify user once SDK is ready
    const interval = setInterval(() => {
      if (window.Canny) {
        identifyUser(user)
        clearInterval(interval)
      }
    }, 500)

    return () => clearInterval(interval)
  }, [user])

  return null
}

// Keep types loose here: we only read a few known fields when present
function identifyUser(user: User | null) {
  if (!user || !window.Canny) return

  try {
    window.Canny('identify', {
      id: user.userId ?? user.email ?? undefined,
      email: user.email || undefined,
      name: user.displayName || undefined,
      // Optional custom fields
      appVersion: config.appVersion,
      env: config.env,
    })
  } catch (e) {
    console.warn('Canny: identify failed', e)
  }
}

// Global type for Canny
declare global {
  interface Window {
    Canny?: ((...args: unknown[]) => void) & { q?: unknown[][] }
  }
}
